import { getScopedDomain } from '../lib/tenantScope';
// Phones — flat list of provisioned phones. Mirrors 3CX shape:
//   • toolbar drives sub-pages and bulk actions (Options modal, PNP Phones,
//     Hot Desking Phones, Reboot, Reprovision, Firmwares)
//   • main table shows: ☐ | Ext | Vendor | Model | Fw Version | Name | MAC | IP
//   • search box + 3-dot row menu

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone, Settings, Wifi, Briefcase, RotateCw, RefreshCw, FileText,
  Search, MoreVertical, Trash2, Copy, X, Save, Radio, Plus, Loader2, ArrowUpCircle,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { bearerHeaders } from '../lib/scopedDomain';
import FirmwareDeployModal from '../components/FirmwareDeployModal';

// DECT base stations (DP750/752/755): infrastructure hosting up to 10 handsets,
// each attached to its own user via the Handsets modal (#799).
const isDectBase = (model: string | null | undefined) =>
  !!model && /\bdp\s*7[15]\d\b/i.test(model) && !/\bdp\s*7[23]\d\b/i.test(model);

export default function Phones() {
  const me = api.me();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [showOptions, setShowOptions] = useState(false);
  const [showFwDeploy, setShowFwDeploy] = useState(false);

  const { data: phones = [] } = useQuery({
    queryKey: ['phones-provisioned', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.devices.list(getScopedDomain(me?.domain_uuid)) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid),
    refetchInterval: 30_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return phones as any[];
    return (phones as any[]).filter(p => {
      const haystack = [p.extension, p.device_vendor, p.device_model, p.device_label, p.device_mac, p.device_ip, p.firmware]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [phones, search]);

  function togglePick(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }
  function togglePickAll() {
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(filtered.map((p: any) => p.device_uuid)));
  }

  const reboot = useMutation({
    mutationFn: (id: string) => api.devices.reboot(id),
  });
  // Ops must target the tenant being VIEWED (same domain the list query uses),
  // not the admin's home tenant — a super_admin browsing another department
  // would otherwise reprovision/delete against the wrong tenant (#942).
  const reprovision = useMutation({
    mutationFn: (id: string) => api.devices.reprovision(getScopedDomain(me?.domain_uuid)!, id),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.devices.remove(getScopedDomain(me?.domain_uuid)!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phones-provisioned', getScopedDomain(me?.domain_uuid)] }),
  });
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [handsetsFor, setHandsetsFor] = useState<any | null>(null);

  function bulkReboot() {
    if (picked.size === 0) return alert('Pick at least one phone to reboot.');
    if (!confirm(`Reboot ${picked.size} phone${picked.size === 1 ? '' : 's'}?`)) return;
    Array.from(picked).forEach(id => reboot.mutate(id));
  }
  function bulkReprovision() {
    if (picked.size === 0) return alert('Pick at least one phone to reprovision.');
    if (!confirm(`Reprovision ${picked.size} phone${picked.size === 1 ? '' : 's'}? They will reload their config.`)) return;
    Array.from(picked).forEach(id => reprovision.mutate(id));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Phone /> Phones</h1>
      </div>

      {/* Toolbar — mirrors 3CX 142312/144825 */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <ToolbarButton icon={<Settings size={14}/>} label="Options" primary onClick={() => setShowOptions(true)} />
        <ToolbarButton icon={<Wifi size={14}/>} label="PNP Phones" primary onClick={() => navigate('/phones/pnp')} />
        <ToolbarButton icon={<Briefcase size={14}/>} label="Hot Desking Phones" primary onClick={() => navigate('/phones/hot-desking')} />
        <ToolbarButton icon={<RotateCw size={14}/>} label="Reboot" onClick={bulkReboot} disabled={picked.size === 0} />
        <ToolbarButton icon={<RefreshCw size={14}/>} label="Reprovision" onClick={bulkReprovision} disabled={picked.size === 0} />
        <ToolbarButton icon={<FileText size={14}/>} label="Firmwares" primary onClick={() => navigate('/phones/firmwares')} />
        <ToolbarButton icon={<ArrowUpCircle size={14}/>} label="Deploy Firmware" onClick={() => setShowFwDeploy(true)} />

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            className="bg-transparent outline-none text-sm w-48"
          />
        </div>
      </div>

      {/* Main list */}
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={picked.size > 0 && picked.size === filtered.length}
                       onChange={togglePickAll} />
              </th>
              <th>Ext</th>
              <th>Vendor</th>
              <th>Model</th>
              <th>Fw Version</th>
              <th>Name</th>
              <th>MAC</th>
              <th>IP</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p: any) => {
              const id = p.device_uuid;
              return (
                <tr key={id} className="hover:bg-elevated">
                  <td><input type="checkbox" checked={picked.has(id)} onChange={() => togglePick(id)} /></td>
                  <td className="font-mono">{p.extension || '—'}</td>
                  <td>{p.device_vendor || '—'}</td>
                  <td>{p.device_model || '—'}</td>
                  <td className="font-mono text-xs">{p.firmware || '—'}</td>
                  <td>{p.device_label || ''}</td>
                  <td className="font-mono text-xs">
                    {p.device_mac || ''}
                    {p.device_mac && (
                      <button title="Copy MAC" className="btn-ghost p-1 ml-1"
                              onClick={() => navigator.clipboard.writeText(p.device_mac)}>
                        <Copy size={10} />
                      </button>
                    )}
                  </td>
                  <td className="font-mono text-xs">{p.device_ip || ''}{p.sbc_name ? ` via SBC ${p.sbc_name}` : ''}</td>
                  <td className="relative">
                    <button className="btn-ghost p-1" title="Actions" onClick={() => setMenuOpen(menuOpen === id ? null : id)}>
                      <MoreVertical size={14} />
                    </button>
                    {menuOpen === id && (
                      <div className="absolute right-2 top-full mt-1 card z-20 min-w-[150px] py-1" onMouseLeave={() => setMenuOpen(null)}>
                        {isDectBase(p.device_model) && (
                          <button className="w-full text-left px-3 py-1.5 hover:bg-elevated text-sm text-primary" onClick={() => { setHandsetsFor(p); setMenuOpen(null); }}>Handsets…</button>
                        )}
                        <button className="w-full text-left px-3 py-1.5 hover:bg-elevated text-sm" onClick={() => { reboot.mutate(id); setMenuOpen(null); }}>Reboot</button>
                        <button className="w-full text-left px-3 py-1.5 hover:bg-elevated text-sm" onClick={() => { reprovision.mutate(id); setMenuOpen(null); }}>Reprovision</button>
                        <button className="w-full text-left px-3 py-1.5 hover:bg-elevated text-sm text-danger" onClick={() => { if (confirm('Delete this phone?')) remove.mutate(id); setMenuOpen(null); }}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-textSecondary">
                {phones.length === 0
                  ? <>No phones provisioned yet. Use <strong>PNP Phones</strong> to discover phones on your LAN, or assign a phone to a user from the user editor.</>
                  : 'No phones match the search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showOptions && <IPPhoneOptionsModal onClose={() => setShowOptions(false)} />}
      <FirmwareDeployModal open={showFwDeploy} onClose={() => setShowFwDeploy(false)}
        tid={getScopedDomain(me?.domain_uuid)} phones={phones as any[]} picked={Array.from(picked)} />
      {handsetsFor && (
        <HandsetsModal device={handsetsFor} tid={getScopedDomain(me?.domain_uuid)!}
          onClose={() => { setHandsetsFor(null); qc.invalidateQueries({ queryKey: ['phones-provisioned', getScopedDomain(me?.domain_uuid)] }); }} />
      )}
    </div>
  );
}

// Handset slots for a DECT base (#799): the base is infrastructure; each of its
// 10 slots attaches one user's DP720/DP730 handset. Changes re-render the base
// config and trigger a re-provision push automatically.
function HandsetsModal({ device, tid, onClose }: { device: any; tid: string; onClose: () => void }) {
  const qc = useQueryClient();
  const hs = useQuery({
    queryKey: ['dect-handsets', device.device_uuid],
    queryFn: () => api.devices.handsets(tid, device.device_uuid),
    refetchInterval: 10_000,
  });
  const users = useQuery({ queryKey: ['users-list-handsets'], queryFn: () => api.users.list() });
  const [pickExt, setPickExt] = useState('');
  const [err, setErr] = useState('');

  const add = useMutation({
    mutationFn: () => api.devices.addHandset(tid, device.device_uuid, pickExt),
    onSuccess: () => { setPickExt(''); setErr(''); qc.invalidateQueries({ queryKey: ['dect-handsets', device.device_uuid] }); },
    onError: (e: any) => setErr(e.message || 'add failed'),
  });
  const del = useMutation({
    mutationFn: (slot: number) => api.devices.removeHandset(tid, device.device_uuid, slot),
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['dect-handsets', device.device_uuid] }); },
    onError: (e: any) => setErr(e.message || 'remove failed'),
  });

  const slots = hs.data?.slots || [];
  const used = hs.data?.used ?? 0;
  const onBase = new Set(slots.filter((s: any) => s.extension).map((s: any) => s.extension));
  const candidates = ((users.data as any[]) || [])
    .filter(u => u.domain_uuid === tid && !onBase.has(u.extension));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-auto" onClick={onClose}>
      <div className="card max-w-lg w-full p-5 space-y-4 mt-10" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radio size={16} className="text-primary" /> Handsets — {device.device_model}
          </h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <div className="text-xs text-textSecondary">
          Base <span className="font-mono">{device.device_mac}</span> · {used} of {hs.data?.max ?? 10} handsets used.
          Each slot registers one DP720/DP730 to its user's extension; the base re-provisions automatically after changes.
        </div>

        {hs.isLoading && <div className="text-sm text-textSecondary flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading slots…</div>}

        {!hs.isLoading && (
          <div className="border border-border rounded-md divide-y divide-border/60">
            {slots.map((s: any) => (
              <div key={s.slot} className="px-3 py-2 flex items-center gap-3 text-sm">
                <span className="font-mono text-xs text-textSecondary w-14 shrink-0">HS {s.slot}</span>
                {s.extension ? (
                  <>
                    <span className="flex-1 min-w-0 truncate">
                      <span className="font-mono">{s.extension}</span>
                      {s.name && <span className="text-textSecondary ml-2">{s.name}</span>}
                    </span>
                    <button className="btn-ghost p-1 text-danger shrink-0" title={`Remove handset ${s.slot}`}
                      disabled={del.isPending}
                      onClick={() => { if (confirm(`Remove ext ${s.extension} from handset slot ${s.slot}?`)) del.mutate(s.slot); }}>
                      <Trash2 size={13} />
                    </button>
                  </>
                ) : (
                  <span className="flex-1 text-textSecondary/60 text-xs">— free —</span>
                )}
              </div>
            ))}
          </div>
        )}

        {used < (hs.data?.max ?? 10) && (
          <div className="flex items-center gap-2">
            <select className="input flex-1" value={pickExt} onChange={e => setPickExt(e.target.value)}>
              <option value="">— Add a user's handset —</option>
              {candidates.map((u: any) => (
                <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name || u.effective_caller_id_name || ''}</option>
              ))}
            </select>
            <button className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
              disabled={!pickExt || add.isPending}
              onClick={() => add.mutate()}>
              {add.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
            </button>
          </div>
        )}
        {err && <div className="text-xs text-danger">{err}</div>}
      </div>
    </div>
  );
}

function ToolbarButton({ icon, label, onClick, disabled, primary }: any) {
  const cls = primary
    ? 'btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40'
    : 'btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40';
  return (
    <button className={cls} onClick={onClick} disabled={disabled}>
      {icon}{label}
    </button>
  );
}

// IP Phone Options modal — matches 3CX 140253 layout.
function IPPhoneOptionsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const cfg = useQuery<any>({
    queryKey: ['ip-phone-options'],
    // Token comes from the central localStorage-backed client (lib/api), like
    // every other request — the old window token global was never assigned
    // anywhere, so these requests silently failed auth (#942).
    queryFn: () => fetch('/api/system/phone-options', { headers: bearerHeaders(getToken()) })
      .then(r => r.ok ? r.json() : ({})).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...defaultPhoneOpts(), ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fetch('/api/system/phone-options', {
      method: 'PUT',
      headers: bearerHeaders(getToken(), true),
      body: JSON.stringify(f),
    }).then(r => r.ok ? r.json() : Promise.reject(new Error('save failed'))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ip-phone-options'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-auto" onClick={onClose}>
      <div className="card max-w-2xl w-full p-6 space-y-5 mt-10" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">IP Phone Options</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Section title="Provisioning">
          <Toggle label="Allow Stratus to provision securely all IP Phones using Remote Provisioning Server (supports LOCAL LAN, STUN, SBC, and Router methods)"
                  v={!!f.rps_enabled} on={v => set('rps_enabled', v)} />
          <Toggle label="Secure device provisioning files using unique keys. Disable if using DHCP Option 66 for provisioning."
                  v={!!f.secure_keys} on={v => set('secure_keys', v)} />
        </Section>

        <Section title="IP Phone Firmware">
          <label className="text-sm flex items-center gap-2">
            <input type="radio" checked={f.firmware_policy === 'supported'} onChange={() => set('firmware_policy', 'supported')} />
            Use Stratus Supported Firmwares (recommended)
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="radio" checked={f.firmware_policy === 'untested'} onChange={() => set('firmware_policy', 'untested')} />
            Use Untested - Unsupported Firmwares
          </label>
          <Toggle label="Update automatically new phones to Stratus supported firmware (BETA)"
                  v={!!f.firmware_auto_update} on={v => set('firmware_auto_update', v)} />
          <Toggle label="Automatically cleanup and delete unused firmwares"
                  v={!!f.firmware_auto_cleanup} on={v => set('firmware_auto_cleanup', v)} />
        </Section>

        <Section title="Reassign DND Button">
          <Toggle label="Reassign default DND button to use profile status"
                  v={!!f.dnd_uses_profile} on={v => set('dnd_uses_profile', v)} />
          {f.dnd_uses_profile && (
            <select className="input mt-1" value={f.dnd_profile || 'Away'} onChange={e => set('dnd_profile', e.target.value)}>
              {['Available', 'Away', 'Do Not Disturb', 'Lunch', 'Business Trip'].map(p => <option key={p}>{p}</option>)}
            </select>
          )}
        </Section>

        <Section title="IP Phone Directory Update">
          <p className="text-xs text-textSecondary mb-2">
            Controls how often IP phones download and refresh their local phonebook from the PBX. Phones periodically request the directory using this interval. Shorter = faster propagation; longer = less network traffic.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <UnitField label="Fanvil Update Interval" value={f.fanvil_min || 720} unit="Min" onValue={v => set('fanvil_min', v)} />
            <UnitField label="SNOM Update Interval"   value={f.snom_sec || 86400} unit="Sec" onValue={v => set('snom_sec', v)} />
            <UnitField label="Yealink Update Interval" value={f.yealink_sec || 21600} unit="Sec" onValue={v => set('yealink_sec', v)} />
            <UnitField label="Grandstream Update Interval" value={f.grandstream_min || 720} unit="Min" onValue={v => set('grandstream_min', v)} />
          </div>
        </Section>

        <Section title="Configure Queue Ringtones">
          <label className="text-sm flex items-center gap-2">
            <input type="radio" checked={f.queue_ringtones === 'default'} onChange={() => set('queue_ringtones', 'default')} />
            Use default ringtone for all queues (recommended for all phones)
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="radio" checked={f.queue_ringtones === 'custom'} onChange={() => set('queue_ringtones', 'custom')} />
            Use custom ringtones per queue: each Agent will configure distinctive ringtones for each queue. Non-configured queues will play the ringtone used for all calls.
          </label>
        </Section>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn">Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
            <Save size={14} /> {save.isPending ? 'Saving…' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

function defaultPhoneOpts() {
  return {
    rps_enabled: true,
    secure_keys: true,
    firmware_policy: 'supported',
    firmware_auto_update: false,
    firmware_auto_cleanup: false,
    dnd_uses_profile: false,
    dnd_profile: 'Away',
    fanvil_min: 720,
    snom_sec: 86400,
    yealink_sec: 21600,
    grandstream_min: 720,
    queue_ringtones: 'default',
  };
}

function Section({ title, children }: any) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold border-b border-border pb-1">{title}</div>
      {children}
    </div>
  );
}
function Toggle({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) {
  return <label className="text-sm flex items-start gap-2 py-1"><input type="checkbox" checked={v} onChange={e => on(e.target.checked)} className="mt-0.5" /> <span>{label}</span></label>;
}
function UnitField({ label, value, unit, onValue }: { label: string; value: number; unit: string; onValue: (v: number) => void }) {
  return (
    <div>
      <label className="block text-xs text-textSecondary mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="number" min={1} className="input flex-1 font-mono" value={value} onChange={e => onValue(parseInt(e.target.value, 10) || 0)} />
        <div className="px-3 py-1.5 bg-elevated border border-border rounded-md text-sm">{unit}</div>
      </div>
    </div>
  );
}
